/**
 * Single source for "operative" motion wording in minutes and live feed (uses current motion.text).
 */

import { VOTE_REQUIREMENTS } from './motionTypes.js';

/**
 * Left-hand category pill in Activity / Observer (e.g. "Point of Order", not generic "Motion").
 */
export function getMinuteEntryKindLabel(entry, motionTypesMap) {
  if (!entry) return 'Entry';

  if (entry.type === 'amendment_result') {
    return motionTypesMap?.AMEND?.name || 'Amendment';
  }

  if (
    entry.type === 'motion_stated' ||
    entry.type === 'motion_completed' ||
    entry.type === 'motion_ready_for_vote'
  ) {
    const mdType = entry.motionData?.type;
    if (mdType && motionTypesMap?.[mdType]) {
      return motionTypesMap[mdType].name;
    }
    return 'Motion';
  }

  const staticLabels = {
    motion_made: 'Motion',
    motion_seconded: 'Second',
    call_to_order: 'Call to Order',
    adjournment: 'Adjournment',
    election: 'Election',
    unanimous_consent: 'Unanimous Consent',
    uc_request: 'UC Request',
    objection: 'Objection',
    agenda_completion: 'Agenda Item',
    roll_call: 'Roll Call',
    motion_effect: 'Business',
    consent_batch_summary: 'Consent agenda',
    consent_item_adopted: 'Consent item',
    consent_batch_rejected: 'Consent agenda',
    consent_agenda_pull: 'Consent calendar',
    chair_note: 'Chair note',
  };

  if (staticLabels[entry.type]) return staticLabels[entry.type];

  return 'Agenda Item';
}

/**
 * Outcome text for activity pills — chair rulings and inquiries, not "PASSED"/"FAILED" where misleading.
 */
export function getMinuteEntryOutcomeLabel(entry, motionTypesMap) {
  const result = entry?.result ?? entry?.motionData?.result;
  if (result == null || result === '') return null;

  const key = entry?.motionData?.type;
  const meta = key && motionTypesMap ? motionTypesMap[key] : null;

  if (key === 'POINT_ORDER') {
    if (result === 'passed' || result === 'passed_by_uc') return 'Well taken';
    if (result === 'failed') return 'Not well taken';
  }

  if (key === 'APPEAL') {
    if (result === 'passed' || result === 'passed_by_uc') return 'Sustained';
    if (result === 'failed') return 'Overruled';
  }

  if (meta?.flowType === 'INFO') {
    if (result === 'passed' || result === 'passed_by_uc') return 'Answered';
    if (result === 'failed') return 'Closed';
  }

  if (
    meta?.voteRequired === VOTE_REQUIREMENTS.CHAIR_DECIDES &&
    key !== 'POINT_ORDER'
  ) {
    if (result === 'passed' || result === 'passed_by_uc') return 'Sustained';
    if (result === 'failed') return 'Overruled';
  }

  if (result === 'passed_by_uc') return 'PASSED (UC)';
  if (result === 'passed') return 'PASSED';
  if (result === 'failed') return 'FAILED';
  if (result === 'referred_to_committee') return 'REFERRED';
  if (result === 'postponed') return 'POSTPONED';
  return String(result).toUpperCase();
}

export function getMinuteEntryOutcomePillClass(entry) {
  const result = entry.result ?? entry.motionData?.result;
  if (result === 'referred_to_committee') return 'bg-blue-100 text-blue-800';
  if (result === 'postponed') return 'bg-indigo-100 text-indigo-800';
  if (result === 'passed' || result === 'passed_by_uc') return 'bg-green-100 text-green-800';
  if (result === 'failed') return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-800';
}

/** Exported minutes already state the outcome in the line; avoid " - ADOPTED" for non-adoptable items. */
export function minuteExportSuppressesAdoptedDefeatedSuffix(entry, motionTypesMap) {
  const key = entry.motionData?.type;
  if (!key || !motionTypesMap?.[key]) return false;
  const mt = motionTypesMap[key];
  if (mt.flowType === 'INFO') return true;
  if (key === 'APPEAL') return true;
  if (mt.voteRequired === VOTE_REQUIREMENTS.CHAIR_DECIDES) return true;
  if (mt.voteRequired === VOTE_REQUIREMENTS.NONE) return true;
  return false;
}

export function formatStatedMotionMinuteLine(motion, motionTypesMap) {
  if (!motion || !motionTypesMap) return '';
  const motionType = motionTypesMap[motion.type];
  if (!motionType) {
    return motion.text || '';
  }
  const secondedByText = motion.secondedBy ? ` (seconded by ${motion.secondedBy})` : '';
  const quote =
    motion.text && motion.text !== motionType.name ? `: "${motion.text}"` : '';
  return `${motion.member} moved ${motionType.name.toLowerCase()}${quote}${secondedByText}`;
}

/**
 * Amendment disposition line, e.g. for the Activity/minutes entry when an amendment is
 * resolved. If a secondary amendment (an amendment to this amendment) altered its wording
 * along the way, call out the original language too — the same "as amended" traceability
 * the main motion gets — so the chain is auditable all the way down.
 */
export function formatAmendmentDispositionText(motion, resultText) {
  const originalText = motion?.originalText;
  const finalText = motion?.text;
  const wasItselfAmended = originalText && finalText && originalText !== finalText;
  if (wasItselfAmended) {
    return `${motion.member} moved to amend by "${originalText}". As amended by a further amendment, it read: "${finalText}". ${resultText}`;
  }
  return `${motion?.member} moved to amend by ${motion?.text}. ${resultText}`;
}

/**
 * Final minutes line when a motion is disposed (passed / failed), matching chair "announce" wording.
 */
export function formatCompletedMotionMinuteLine(motion, motionTypesMap, finalResultWord = 'passed') {
  if (!motion || !motionTypesMap?.[motion.type]) return motion?.text || '';
  const motionType = motionTypesMap[motion.type];
  const secondedByText = motion.secondedBy ? ` (seconded by ${motion.secondedBy})` : '';
  if (motion.type === 'AMEND') {
    return formatAmendmentDispositionText(motion, `Amendment ${finalResultWord}.`);
  }
  if (motion.type === 'MAIN') {
    const quote =
      motion.text && motion.text !== motionType.name ? `: "${motion.text}"` : '';
    return `${motion.member} moved ${motionType.name.toLowerCase()}${quote}${secondedByText}. Motion ${finalResultWord}.`;
  }
  const quote =
    motion.text && motion.text !== motionType.name ? `: "${motion.text}"` : '';
  return `${motion.member} moved ${motionType.name.toLowerCase()}${quote}${secondedByText}. Motion ${finalResultWord}.`;
}

/**
 * Second minutes line at disposition time (stated intro already recorded earlier).
 * Keeps chronology readable when amendments intervened.
 */
export function formatDispositionMinuteLine(motion, motionTypesMap, finalResultWord = 'passed') {
  if (!motion || !motionTypesMap?.[motion.type]) {
    return `The motion ${finalResultWord}.`;
  }
  const motionType = motionTypesMap[motion.type];

  if (motionType?.flowType === 'INFO' && motion.type === 'PARL_INQUIRY') {
    return `A parliamentary inquiry was raised: "${motion.text}". The chair responded.`;
  }
  if (motionType?.flowType === 'CHAIR' && motion.type === 'POINT_ORDER') {
    return `The chair ruled the point of order ${
      finalResultWord === 'passed' ? 'well taken' : 'not well taken'
    }.`;
  }
  if (motionType?.flowType === 'CHAIR' && motion.type === 'APPEAL') {
    return `The decision of the chair was ${
      finalResultWord === 'passed' ? 'sustained' : 'overruled'
    }.`;
  }

  if (motion.type === 'MAIN') {
    const passedAmendments = (motion.amendments || []).filter((a) => a.result === 'passed');
    const originalText = motion.originalText;
    const finalText = motion.text;
    const asAmended =
      passedAmendments.length > 0 &&
      ((originalText && finalText && originalText !== finalText) || Boolean(motion.isAmended));

    if (asAmended && finalText) {
      return `The main motion as amended ("${finalText}") ${finalResultWord}.`;
    }
    if (finalText && finalText !== motionType.name) {
      return `The main motion ("${finalText}") ${finalResultWord}.`;
    }
    return `The main motion ${finalResultWord}.`;
  }

  const label = motionType.name.toLowerCase();
  const quote =
    motion.text && motion.text !== motionType.name ? ` ("${motion.text}")` : '';
  return `The ${label}${quote} ${finalResultWord}.`;
}

export function getMinuteEntryDisplayText(entry, motionTypesMap) {
  if (!entry) return '';
  if (entry.type === 'chair_note') return entry.text || '';
  if (entry.type === 'election' && entry.exportText) return entry.exportText;

  // Stated / pending MAIN: keep the historical stated line frozen (do not follow live amended text).
  if (
    entry.motionData?.type === 'MAIN' &&
    (entry.type === 'motion_stated' ||
      entry.type === 'motion_ready_for_vote' ||
      entry.status === 'pending' ||
      entry.status === 'amended') &&
    entry.type !== 'motion_completed'
  ) {
    if (entry.text) return entry.text;
    const md = entry.motionData;
    const snapshot = {
      ...md,
      text: md.originalText || md.text,
    };
    return formatStatedMotionMinuteLine(snapshot, motionTypesMap);
  }

  if (
    entry.type === 'consent_batch_summary' ||
    entry.type === 'consent_item_adopted' ||
    entry.type === 'consent_batch_rejected' ||
    entry.type === 'consent_agenda_pull'
  ) {
    return entry.text || '';
  }

  return entry.text || '';
}

/** Stated / pending MAIN line (not the disposition `_result` row). */
export function isMainMotionStatedActivityEntry(entry) {
  if (!entry || entry.motionData?.type !== 'MAIN') return false;
  if (entry.type === 'motion_completed') return false;
  if (String(entry.id || '').endsWith('_result')) return false;
  return (
    entry.type === 'motion_stated' ||
    entry.type === 'motion_ready_for_vote' ||
    entry.status === 'pending' ||
    entry.status === 'amended'
  );
}

/** MAIN motion IDs that already have a disposition (completed) Activity row. */
export function getDisposedMainMotionIds(minutes = []) {
  const ids = new Set();
  for (const entry of minutes) {
    if (!entry) continue;
    const motionId = entry.motionData?.id;
    if (!motionId || entry.motionData?.type !== 'MAIN') continue;
    const isDisposition =
      entry.type === 'motion_completed' || String(entry.id || '').endsWith('_result');
    if (isDisposition) ids.add(motionId);
  }
  return ids;
}

/**
 * Activity-only: hide stated MAIN once disposition exists (export keeps both).
 */
export function shouldShowMinuteEntryInActivity(entry, disposedMainIds) {
  if (!entry) return false;
  if (!isMainMotionStatedActivityEntry(entry)) return true;
  const motionId = entry.motionData?.id;
  if (!motionId) return true;
  return !disposedMainIds?.has(motionId);
}

/**
 * Nest subsidiaries under disposition when present, else under stated MAIN.
 * Prevents Amend children rendering twice under both top-level MAIN rows.
 */
export function isCanonicalNestParentForActivity(entry, disposedMainIds) {
  if (!entry?.motionData?.id) return false;
  if (entry.motionData.type !== 'MAIN') return true;
  const motionId = entry.motionData.id;
  const hasDisposition = disposedMainIds?.has(motionId);
  if (hasDisposition) {
    return (
      entry.type === 'motion_completed' || String(entry.id || '').endsWith('_result')
    );
  }
  return true;
}

/**
 * Original wording subtext for Activity/Observer when a MAIN motion or a pending
 * amendment (itself altered by a secondary amendment) was amended — same "Originally: …"
 * traceability at every level of the amendment chain, not just at the main motion.
 * MAIN's stated line is excluded since it already freezes the original make/state
 * wording as primary text; AMEND has no such frozen stated line (only a disposition
 * entry), so no equivalent exclusion is needed there.
 * Returns null when there is nothing to show (failed amend / unamended / stated).
 */
export function getMinuteEntryOriginalSubtext(entry) {
  const md = entry?.motionData;
  if (!md || (md.type !== 'MAIN' && md.type !== 'AMEND')) return null;
  if (md.type === 'MAIN' && isMainMotionStatedActivityEntry(entry)) return null;

  const original = String(md.originalText || '').trim();
  if (!original) return null;
  const operative = String(md.text || '').trim();
  const amended = Boolean(md.isAmended) || (operative && original !== operative);
  if (!amended) return null;
  if (operative && original === operative) return null;
  return `Originally: "${original}"`;
}

/**
 * Build Activity hierarchy: filtered top-level + nest map keyed by motion id
 * (children only attached via isCanonicalNestParentForActivity at render time).
 */
export function buildActivityMinuteGroups(minutes = []) {
  const sortedMinutes = [...minutes].sort(
    (a, b) => (b.chronologicalOrder || 0) - (a.chronologicalOrder || 0)
  );
  const disposedMainIds = getDisposedMainMotionIds(sortedMinutes);
  const groupedMinutes = sortedMinutes.reduce((acc, entry) => {
    const parentId = entry.parentMotionId || 'top-level';
    if (!acc[parentId]) acc[parentId] = [];
    acc[parentId].push(entry);
    return acc;
  }, {});

  const topLevel = (groupedMinutes['top-level'] || []).filter((entry) =>
    shouldShowMinuteEntryInActivity(entry, disposedMainIds)
  );

  return { sortedMinutes, groupedMinutes, disposedMainIds, topLevel };
}
